import React, { useState, useEffect } from 'react';
import { Send, Loader2, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

function A2A() {
  const [fromAgent, setFromAgent] = useState('');
  const [toAgent, setToAgent] = useState('');
  const [message, setMessage] = useState('');
  const [context, setContext] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);

  useEffect(() => {
    // Fetch available agents on mount
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const res = await axios.get('https://nanda-testbed-production.up.railway.app/api/agents');
      setAvailableAgents(res.data.agents || []);
    } catch (err) {
      console.error('Error fetching agents:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  const sendA2AMessage = async () => {
    if (!fromAgent.trim() || !toAgent.trim() || !message.trim()) {
      setError('Please fill in From Agent, To Agent, and Message fields');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      const res = await axios.post(
        'https://nanda-testbed-production.up.railway.app/api/a2a',
        {
          from_agent: fromAgent,
          to_agent: toAgent,
          message: message,
          context: context || undefined
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 60000 // 60 second timeout for A2A
        }
      );

      setResponse(res.data);
    } catch (err) {
      console.error('Error sending A2A message:', err);
      
      let errorMsg = 'Failed to send A2A message';
      
      if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.response?.data?.detail) {
        errorMsg = typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : JSON.stringify(err.response.data.detail);
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      sendA2AMessage();
    }
  };

  return (
    <div style={{
      minHeight: 'calc(100vh - 56px)',
      background: '#000000',
      padding: '40px 20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start'
    }}>
      <div style={{
        maxWidth: '800px',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            margin: '0 0 8px 0',
            fontSize: '32px',
            fontWeight: '600',
            color: '#ffffff'
          }}>
            Agent-to-Agent Communication
          </h1>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.6)',
            lineHeight: '1.6'
          }}>
            Send messages between agents through the NANDA testbed. Agent 1 will communicate with Agent 2 and return the response.
          </p>
        </div>

        {/* Available Agents Info */}
        {!loadingAgents && availableAgents.length > 0 && (
          <div style={{
            background: 'rgba(139, 32, 32, 0.1)',
            border: '1px solid rgba(139, 32, 32, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '500',
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '8px'
            }}>
              Available Agents ({availableAgents.length}):
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {availableAgents.map(agent => (
                <div
                  key={agent.id}
                  style={{
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                  title={agent.description}
                >
                  <strong>{agent.username}</strong> - {agent.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '32px'
        }}>
          {/* From Agent & To Agent - Side by Side */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '16px',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            {/* From Agent */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255, 255, 255, 0.9)'
              }}>
                From Agent <span style={{ color: '#8b2020' }}>*</span>
              </label>
              <input
                type="text"
                value={fromAgent}
                onChange={(e) => setFromAgent(e.target.value)}
                placeholder="maria-agent"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#8b2020';
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
              />
            </div>

            {/* Arrow */}
            <div style={{ paddingTop: '24px' }}>
              <ArrowRight size={24} style={{ color: '#8b2020' }} />
            </div>

            {/* To Agent */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: 'rgba(255, 255, 255, 0.9)'
              }}>
                To Agent <span style={{ color: '#8b2020' }}>*</span>
              </label>
              <input
                type="text"
                value={toAgent}
                onChange={(e) => setToAgent(e.target.value)}
                placeholder="agent_1"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#8b2020';
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
              />
            </div>
          </div>

          {/* Message */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              Message <span style={{ color: '#8b2020' }}>*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="What is the weather forecast for tomorrow?"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
                minHeight: '100px',
                fontFamily: 'inherit',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#8b2020';
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            />
          </div>

          {/* Context (Optional) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              Context <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>(optional)</span>
            </label>
            <input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Additional context for the request"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#8b2020';
                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
            />
          </div>

          {/* Send Button */}
          <button
            onClick={sendA2AMessage}
            disabled={isLoading || !fromAgent.trim() || !toAgent.trim() || !message.trim()}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: (fromAgent.trim() && toAgent.trim() && message.trim() && !isLoading) 
                ? '#8b2020' 
                : 'rgba(139, 32, 32, 0.3)',
              color: (fromAgent.trim() && toAgent.trim() && message.trim() && !isLoading) 
                ? '#ffffff' 
                : 'rgba(255, 255, 255, 0.4)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: (fromAgent.trim() && toAgent.trim() && message.trim() && !isLoading) 
                ? 'pointer' 
                : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (fromAgent.trim() && toAgent.trim() && message.trim() && !isLoading) {
                e.currentTarget.style.background = '#6b1818';
              }
            }}
            onMouseLeave={(e) => {
              if (fromAgent.trim() && toAgent.trim() && message.trim() && !isLoading) {
                e.currentTarget.style.background = '#8b2020';
              }
            }}
          >
            {isLoading ? (
              <>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Sending A2A Message...
              </>
            ) : (
              <>
                <Send size={20} />
                Send A2A Message
              </>
            )}
          </button>

          <p style={{
            marginTop: '12px',
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.5)',
            textAlign: 'center',
            fontStyle: 'italic'
          }}>
            Press Ctrl+Enter to send
          </p>
        </div>

        {/* Response Display */}
        {response && (
          <div style={{
            marginTop: '24px',
            background: 'rgba(139, 32, 32, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(139, 32, 32, 0.3)',
            padding: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <CheckCircle size={24} style={{ color: '#8b2020' }} />
              <h2 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#ffffff'
              }}>
                Response Received
              </h2>
            </div>

            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '8px',
              padding: '16px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <pre style={{
                margin: 0,
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace'
              }}>
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {response.processing_time && (
              <div style={{
                marginTop: '12px',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.6)'
              }}>
                Processing time: {response.processing_time.toFixed(2)}s
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div style={{
            marginTop: '24px',
            background: 'rgba(211, 47, 47, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(211, 47, 47, 0.3)',
            padding: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <AlertCircle size={20} style={{ color: '#ff6b6b', flexShrink: 0 }} />
              <div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#ff6b6b',
                  marginBottom: '4px'
                }}>
                  Error
                </div>
                <div style={{
                  fontSize: '13px',
                  color: 'rgba(255, 107, 107, 0.9)',
                  lineHeight: '1.5'
                }}>
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add spinning animation for loading */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

export default A2A;



